/**
 * Trade search helper (2026-08-28) — Pro block on the Potential Trades pane.
 *
 * Pick a dex variant, tap the circle copy button, and a Pokémon GO search
 * string lands on the clipboard: "34,150,151,177,947&!traded". Paste it into
 * GO's search box and everything it shows from your storage is a trade this
 * friend needs.
 *
 * TWO LISTS (2026-09-19 owner ask): "What I can give" is the narrow one, the
 * leads Nexus Dex already matched against your dex (the "you have, they
 * don't" rows). "All they're missing" is the friend's whole gap list for that
 * dex, which finds copies your dex never registered as well. Same string
 * format either way.
 *
 * THE PICTURE (owner direction: the block did not look like what it does):
 * the strip above the controls draws the friend's actual gaps as silhouettes,
 * the string in the middle, and the same species lit up in colour under a
 * magnifier on the right. Real species from the current list, not stock art,
 * so a Shiny list shows the shinies they need.
 *
 * Basic sees the controls blurred and inert with the unlock CTA: the picture
 * stays sharp, because it is the sales pitch (source "trade-search-helper").
 */
import { useMemo, useState } from "react";
import { ArrowRight, Check, ClipboardCopy, Search, UserRound } from "lucide-react";

import { CaughtSprite } from "@/components/CaughtSprite";
import { DEX_VARIANT_META } from "@/components/dexVariantVisuals";
import {
  VARIANT_ORDER,
  type CompareVariant,
  type LiveCompare,
  type PanelRow,
} from "@/components/trade/compareTypes";
import { useToast } from "@/hooks/use-toast";
import { copyToClipboard } from "@/lib/copyToClipboard";
import { friendLineColor } from "@/lib/dexHistoryChart";
import { friendMissingList, searchNumberCount, searchNumbers, tradeSearchList } from "@/lib/tradeSearchCopy";

export type TradeSearchScope = "give" | "all";

interface TradeSearchHelperProps {
  friendName: string;
  /** For the friend's colour in the picture; null draws a neutral avatar. */
  friendAccountId?: number | null;
  /** The full "you have, they don't" rows (unfiltered; Pro gets the full list). */
  rows: PanelRow[];
  /** The live comparison, for the friend's whole gap list. */
  live?: LiveCompare;
  isPro: boolean;
  onUnlock: () => void;
  className?: string;
}

/** Species shown in the picture; the first few of the list, dex order. */
const STORY_SPRITES = 3;
/** Stand-ins when the list is empty, so the picture still explains itself. */
const STORY_FALLBACK_IDS = [25, 133, 143];

export function TradeSearchHelper({
  friendName,
  friendAccountId,
  rows,
  live,
  isPro,
  onUnlock,
  className,
}: TradeSearchHelperProps) {
  const { toast } = useToast();
  const [variant, setVariant] = useState<CompareVariant>("pokemon");
  const [scope, setScope] = useState<TradeSearchScope>("give");
  const [copied, setCopied] = useState(false);

  const giveList = useMemo(() => tradeSearchList(rows, variant), [rows, variant]);
  const allList = useMemo(() => friendMissingList(live, variant) ?? "", [live, variant]);
  const list = scope === "give" ? giveList : allList;
  const count = searchNumberCount(list);
  const storyIds = useMemo(() => {
    const ids = searchNumbers(list).slice(0, STORY_SPRITES);
    return ids.length > 0 ? ids : STORY_FALLBACK_IDS;
  }, [list]);
  const storyIsReal = count > 0;

  async function onCopy() {
    if (list.length === 0) return;
    const ok = await copyToClipboard(list);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast({
        title: "GO search copied",
        description:
          scope === "give"
            ? `${count} dex number${count === 1 ? "" : "s"} ${friendName} is missing that you can trade. Paste it into Pokémon GO's search.`
            : `Every ${DEX_VARIANT_META[variant].label} ${friendName} is missing: ${count} dex number${count === 1 ? "" : "s"}. Paste it into Pokémon GO's search.`,
      });
    } else {
      toast({ title: "Couldn't copy", description: "Try again." });
    }
  }

  const label = DEX_VARIANT_META[variant].label;
  const isShinyList = variant === "shiny" || variant === "shinyLucky";

  return (
    <section
      className={`v2-neon-card-soft p-4 ${className ?? ""}`}
      data-testid="section-trade-search-helper"
    >
      <h2
        className="text-[12px] font-black uppercase tracking-[0.14em] inline-flex items-center gap-1.5"
        style={{ color: "var(--text-ink-faint)" }}
      >
        <Search className="w-3.5 h-3.5" style={{ color: "var(--accent-fill)" }} />
        Trade search helper
      </h2>
      <p className="mt-1.5 text-[11.5px] leading-relaxed" style={{ color: "var(--text-ink-faint)" }}>
        One paste into Pokémon GO's search and your storage shows only the Pokémon {friendName} still
        needs.
      </p>

      {/* ── The picture: their gaps, the string, your box ─────────────── */}
      <div
        className="mt-3 rounded-xl px-3 py-3 grid items-center gap-1"
        style={{
          gridTemplateColumns: "1fr auto 1fr auto 1fr",
          background: "var(--surface-raised)",
          border: "1px solid var(--border-subtle)",
        }}
        data-testid="trade-search-helper-story"
        aria-label={`How it works: ${friendName}'s missing Pokémon become one search that lights them up in your storage`}
      >
        {/* Their gaps: silhouettes, with the friend's mark. */}
        <div className="min-w-0 flex flex-col items-center gap-1">
          <div className="relative flex items-end justify-center -space-x-2">
            {storyIds.map((id, i) => (
              <div
                key={`gap-${id}`}
                className="relative"
                style={{ zIndex: STORY_SPRITES - i, filter: "brightness(0) opacity(0.45)" }}
                aria-hidden
              >
                <CaughtSprite id={id} size={34} shiny={isShinyList} />
              </div>
            ))}
            <span
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full grid place-items-center text-[9px] font-black"
              style={{
                background: friendAccountId != null ? friendLineColor(friendAccountId) : "var(--surface-sunken)",
                color: friendAccountId != null ? "#0B0E1A" : "var(--text-secondary)",
                border: "1px solid var(--surface-raised)",
              }}
              aria-hidden
            >
              {friendAccountId != null ? (friendName[0] ?? "?").toUpperCase() : <UserRound className="w-3 h-3" />}
            </span>
          </div>
          <p className="text-[9.5px] font-bold uppercase tracking-wide text-center" style={{ color: "var(--text-tertiary)" }}>
            {friendName} lacks
          </p>
        </div>

        <ArrowRight className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />

        {/* The string: the numbers, as a chip. */}
        <div className="min-w-0 flex flex-col items-center gap-1">
          <span
            className="max-w-full truncate rounded-full px-2.5 py-1 font-mono text-[10.5px] font-bold tabular-nums"
            style={{
              background: "var(--accent-fill-glow-faint)",
              border: "1px solid var(--accent-fill-glow-subtle)",
              color: "var(--text-ink-bright)",
            }}
            data-testid="trade-search-helper-story-string"
          >
            {storyIsReal ? `${storyIds.join(",")}${count > STORY_SPRITES ? ",…" : ""}` : "34,150,177"}
          </span>
          <p className="text-[9.5px] font-bold uppercase tracking-wide text-center" style={{ color: "var(--text-tertiary)" }}>
            one search
          </p>
        </div>

        <ArrowRight className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />

        {/* Your box: the same species, in colour, under the magnifier. */}
        <div className="min-w-0 flex flex-col items-center gap-1">
          <div className="relative flex items-end justify-center -space-x-2">
            {storyIds.map((id, i) => (
              <div key={`box-${id}`} className="relative" style={{ zIndex: STORY_SPRITES - i }} aria-hidden>
                <CaughtSprite id={id} size={34} shiny={isShinyList} />
              </div>
            ))}
            <span
              className="absolute -bottom-1 -right-1.5 w-5 h-5 rounded-full grid place-items-center"
              style={{
                background: "var(--accent-fill)",
                color: "var(--text-on-accent)",
                boxShadow: "0 0 8px var(--accent-fill-glow)",
              }}
              aria-hidden
            >
              <Search className="w-3 h-3" />
            </span>
          </div>
          <p className="text-[9.5px] font-bold uppercase tracking-wide text-center" style={{ color: "var(--text-tertiary)" }}>
            in your box
          </p>
        </div>
      </div>

      <div className="relative mt-3">
        <div
          style={
            isPro
              ? undefined
              : { filter: "blur(5px)", pointerEvents: "none", userSelect: "none" }
          }
          aria-hidden={!isPro}
          data-testid="trade-search-helper-controls"
        >
          {/* Which list. */}
          <div
            role="radiogroup"
            aria-label="Which numbers to copy"
            className="grid grid-cols-2 gap-1 rounded-full p-0.5"
            style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}
          >
            {(
              [
                { key: "give", label: "What I can give", hint: giveList },
                { key: "all", label: "All they're missing", hint: allList },
              ] as const
            ).map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="radio"
                aria-checked={scope === opt.key}
                onClick={() => setScope(opt.key)}
                disabled={!isPro}
                data-testid={`trade-search-helper-scope-${opt.key}`}
                className="rounded-full px-2 py-1.5 text-[11px] font-bold inline-flex items-center justify-center gap-1.5"
                style={
                  scope === opt.key
                    ? { background: "var(--accent-fill)", color: "var(--text-on-accent)" }
                    : { color: "var(--text-secondary)" }
                }
              >
                {opt.label}
                <span
                  className="rounded-full px-1.5 text-[10px] tabular-nums"
                  style={{
                    background: scope === opt.key ? "hsl(0 0% 0% / 0.18)" : "var(--surface-raised)",
                  }}
                >
                  {searchNumberCount(opt.hint)}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-2 flex items-center gap-2.5">
            <select
              value={variant}
              onChange={(e) => setVariant(e.target.value as CompareVariant)}
              disabled={!isPro}
              aria-label="Dex variant to copy"
              data-testid="trade-search-helper-variant"
              className="flex-1 min-w-0 rounded-xl px-3 text-[13px] font-semibold appearance-none"
              style={{
                height: 44,
                background: "var(--surface-raised)",
                border: "1px solid var(--border-subtle)",
                color: "var(--text-ink-bright)",
              }}
            >
              {VARIANT_ORDER.map((v) => (
                <option key={v} value={v}>
                  {DEX_VARIANT_META[v].label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => void onCopy()}
              disabled={!isPro || list.length === 0}
              aria-label={
                scope === "give"
                  ? `Copy the ${label} dex numbers ${friendName} is missing that you can trade`
                  : `Copy every ${label} dex number ${friendName} is missing`
              }
              data-testid="trade-search-helper-copy"
              className="shrink-0 rounded-full grid place-items-center transition-transform active:scale-95"
              style={{
                width: 44,
                height: 44,
                background:
                  list.length === 0
                    ? "var(--surface-raised)"
                    : "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
                border: list.length === 0 ? "1px solid var(--border-subtle)" : "none",
                color: list.length === 0 ? "var(--text-tertiary)" : "var(--text-on-accent)",
                boxShadow: list.length === 0 ? "none" : "0 0 10px var(--accent-fill-glow)",
              }}
            >
              {copied ? <Check style={{ width: 18, height: 18 }} /> : (
                <ClipboardCopy style={{ width: 18, height: 18 }} />
              )}
            </button>
          </div>
        </div>

        {isPro && (
          <p className="mt-2 text-[10.5px]" style={{ color: "var(--text-tertiary)" }} data-testid="trade-search-helper-count">
            {count === 0
              ? scope === "give"
                ? `Nothing to copy: no ${label} leads ${friendName} needs from you.`
                : live
                  ? `${friendName} is missing no ${label} Pokémon a trade could fill.`
                  : "Loading the comparison."
              : scope === "give"
                ? `${count} dex number${count === 1 ? "" : "s"} ready to copy.`
                : `${count} dex number${count === 1 ? "" : "s"}: everything ${friendName} lacks in the ${label} dex, whether or not Nexus Dex has seen your copy.`}
          </p>
        )}

        {!isPro && (
          <div className="absolute inset-0 grid place-items-center">
            <button
              type="button"
              onClick={onUnlock}
              data-testid="trade-search-helper-unlock"
              className="rounded-full px-4 py-2 text-xs font-bold transition-transform active:scale-95"
              style={{
                background:
                  "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
                color: "var(--text-on-accent)",
                boxShadow: "0 0 10px var(--accent-fill-glow)",
              }}
            >
              Unlock with Pro
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
